import React, { useContext, useEffect, useState } from 'react';
import { StyleSheet, TouchableHighlight, View } from 'react-native';
import DyteText from './DyteText';
import { SvgXml } from 'react-native-svg';
import { defaultIconPack, IconPack } from '../lib/icons';
import DyteClient from '@dytesdk/web-core';
import { DyteI18n, useLanguage } from '../lib/lang';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  size,
  variant,
  iconPack = defaultIconPack,
  t,
}: {
  meeting: DyteClient;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'button' | 'horizontal';
  iconPack?: IconPack;
  t?: DyteI18n;
}) {
  iconPack = iconPack ?? defaultIconPack;
  const canProduceVideoInitialValue = () => {
    const { self, meta, stage } = meeting;
    let _canProduceVideo = self.permissions.canProduceVideo === 'ALLOWED';
    const isWebinar = meta.viewType === 'WEBINAR';
    if (!_canProduceVideo && isWebinar) {
      const canRequestVideo =
        self.permissions.canProduceVideo === 'CAN_REQUEST';
      // If the has approved request to present
      if (
        canRequestVideo &&
        (stage.status === 'ON_STAGE' ||
          stage.status === 'ACCEPTED_TO_JOIN_STAGE')
      ) {
        _canProduceVideo = true;
      }
    }
    return _canProduceVideo;
  };
  const videoEnabled = useDyteSelector((m) => m.self.videoEnabled);
  const [canProduceVideo, setCanProduceVideo] = useState(
    canProduceVideoInitialValue()
  );
  const mediaVideoPermission = useDyteSelector(
    (m) => m.self.mediaPermissions.video
  );
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  useEffect(() => {
    const updateCanProduceVideo = () => {
      const { self, meta, stage } = meeting;
      let _canProduceVideo = self.permissions.canProduceVideo === 'ALLOWED';
      const isWebinar = meta.viewType === 'WEBINAR';
      if (!_canProduceVideo && isWebinar) {
        const canRequestVideo =
          self.permissions.canProduceVideo === 'CAN_REQUEST';
        // If the has approved request to present
        if (
          canRequestVideo &&
          (stage.status === 'ON_STAGE' ||
            stage.status === 'ACCEPTED_TO_JOIN_STAGE')
        ) {
          _canProduceVideo = true;
        }
      }
      setCanProduceVideo(_canProduceVideo);
    };
    updateCanProduceVideo();
    meeting.stage.addListener('stageStatusUpdate', updateCanProduceVideo);

    return () => {
      meeting.stage.removeListener('stageStatusUpdate', updateCanProduceVideo);
    };
  }, [meeting, canProduceVideo]);
  let buttonSize = 25;
  let buttonHorizontal = false;
  switch (size) {
    case 'md':
      buttonSize = 50;
      break;
    case 'lg':
      buttonSize = 100;
      break;
    case 'xl':
      buttonSize = 150;
      break;
  }
  switch (variant) {
    case 'horizontal':
      buttonHorizontal = true;
      break;
  }
  const toggleVideo = async () => {
    if (mediaVideoPermission === 'DENIED') {
      setStates({
        ...storeStates,
        permissionGranted: false,
      });
      return;
    }
    if (videoEnabled) meeting.self.disableVideo();
    else await meeting.self.enableVideo();
  };
  const styles = StyleSheet.create({
    iconStyle: {
      height: 56,
      width: 56,
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
    },
    buttonContainer: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    disabledVideoStyle: {
      marginLeft: 8,
      fontSize: 11,
      marginTop: 10,
    },
  });
  if (!canProduceVideo) return null;
  return (
    <View style={styles.buttonContainer}>
      {buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={toggleVideo}
          style={styles.iconStyle}
        >
          {videoEnabled ? (
            <View style={styles.icon}>
              <SvgXml
                xml={iconPack.video_on.replace('currentColor', colors.text)}
                width={buttonSize}
                height={buttonSize}
              />
              {/* eslint-disable-next-line react-native/no-inline-styles*/}
              <DyteText style={{ fontSize: 11, marginTop: 10 }}>
                {t('video_off')}
              </DyteText>
            </View>
          ) : (
            <View style={styles.icon}>
              <SvgXml
                xml={iconPack.video_off.replace('currentColor', colors.danger)}
                width={buttonSize}
                height={buttonSize}
              />
              <DyteText style={styles.disabledVideoStyle}>
                {t('video_off')}
              </DyteText>
            </View>
          )}
        </TouchableHighlight>
      )}
      {!buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={toggleVideo}
          style={styles.iconStyle}
        >
          {videoEnabled ? (
            <SvgXml
              xml={iconPack.video_on.replace('currentColor', colors.text)}
              width={buttonSize}
              height={buttonSize}
            />
          ) : (
            <SvgXml
              xml={iconPack.video_off.replace('currentColor', colors.danger)}
              width={buttonSize}
              height={buttonSize}
            />
          )}
        </TouchableHighlight>
      )}
      {mediaVideoPermission !== 'ACCEPTED' && (
        <View
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            position: 'absolute',
            top: 3,
            right: 3,
          }}
        >
          <SvgXml
            xml={iconPack.warning.replace('currentColor', colors.warning)}
            width={15}
            height={15}
          />
        </View>
      )}
    </View>
  );
}
